/**
 * Persistent upload queue backed by IndexedDB.
 * If the browser tab closes mid-upload, queued items survive and retry on next open.
 */
import { openDB, type IDBPDatabase } from 'idb'
import type { UploadQueueItem, UploadStatus } from '@/types'

const DB_NAME    = 'the-room-queue'
const DB_VERSION = 1
const STORE      = 'uploads'

let db: IDBPDatabase | null = null

async function getDB(): Promise<IDBPDatabase> {
  if (db) return db
  db = await openDB(DB_NAME, DB_VERSION, {
    upgrade(database) {
      if (!database.objectStoreNames.contains(STORE)) {
        const store = database.createObjectStore(STORE, { keyPath: 'id' })
        store.createIndex('status',    'status')
        store.createIndex('createdAt', 'createdAt')
      }
    },
  })
  return db
}

export async function queueAdd(item: Omit<UploadQueueItem, 'status' | 'progress' | 'retries' | 'createdAt'>): Promise<UploadQueueItem> {
  const database = await getDB()
  const full: UploadQueueItem = {
    ...item,
    status:    'queued',
    progress:  0,
    retries:   0,
    createdAt: Date.now(),
  }
  await database.put(STORE, full)
  return full
}

export async function queueUpdate(id: string, patch: Partial<UploadQueueItem>): Promise<void> {
  const database = await getDB()
  const existing = await database.get(STORE, id)
  if (!existing) return
  await database.put(STORE, { ...existing, ...patch })
}

export async function queueGetAll(): Promise<UploadQueueItem[]> {
  const database = await getDB()
  return database.getAll(STORE)
}

export async function queueGetPending(): Promise<UploadQueueItem[]> {
  const all = await queueGetAll()
  return all.filter(i => i.status === 'queued' || i.status === 'error')
}

export async function queueRemove(id: string): Promise<void> {
  const database = await getDB()
  await database.delete(STORE, id)
}

export async function queueClearCompleted(): Promise<void> {
  const database = await getDB()
  const all = await database.getAll(STORE)
  const tx  = database.transaction(STORE, 'readwrite')
  await Promise.all(
    all.filter(i => i.status === 'done').map(i => tx.store.delete(i.id))
  )
  await tx.done
}

export async function queueStatus(): Promise<Record<UploadStatus, number>> {
  const all = await queueGetAll()
  const counts: Record<UploadStatus, number> = {
    queued: 0, uploading: 0, saving: 0, routing: 0,
    done: 0, error: 0, retrying: 0,
  }
  all.forEach(i => { counts[i.status] = (counts[i.status] ?? 0) + 1 })
  return counts
}
